import React, { useState, useRef, useEffect, useMemo } from 'react';
import { 
  Box, 
  Paper, 
  IconButton, 
  Typography,
  Button,
  Popover,
  ButtonGroup,
  useTheme,
  useMediaQuery,
  Tooltip,
  Fab
} from '@mui/material';
import { 
  FilterList, 
  AccessTime, 
  Check 
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { getTranslation } from '../../i18n';

const FilterPanel = ({ timeFilter, onTimeFilterChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const buttonRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { language } = useSelector((state) => state.ui);
  const translations = getTranslation(language);
  const t = translations.map.filters;

  // Time filter options in milliseconds - memoized to prevent recreation on every render
  const timeFilterOptions = useMemo(() => [
    { value: 600000, label: t.last10min, icon: <AccessTime fontSize="small" /> },
    { value: 3600000, label: t.lastHour, icon: <AccessTime fontSize="small" /> },
    { value: 21600000, label: t.last6hours, icon: <AccessTime fontSize="small" /> },
    { value: 86400000, label: t.last24hours, icon: <AccessTime fontSize="small" /> },
  ], [t.last10min, t.lastHour, t.last6hours, t.last24hours]);

  // Handle filter button click
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle close popover
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Apply selected filter
  const applyFilter = (value) => {
    onTimeFilterChange(value);
    handleClose();
  };

  // Get current filter label
  const getCurrentFilterLabel = () => {
    return timeFilterOptions.find(opt => opt.value === timeFilter)?.label || t.filter;
  };

  // Check if popover is open
  const open = Boolean(anchorEl);
  const id = open ? 'filter-popover' : undefined;

  // Mobile filter button position
  const mobilePosition = {
    position: 'absolute',
    left: 16,
    bottom: 24,
    zIndex: 1000
  };

  // Desktop filter button position
  const desktopPosition = {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 1000
  };

  // Render filter button based on device
  const renderFilterButton = () => {
    if (isMobile) {
      return (
        <Box sx={mobilePosition}>
          <Tooltip title={getCurrentFilterLabel()}>
            <Fab
              ref={buttonRef}
              color="primary"
              aria-label={t.openFilters}
              onClick={handleClick}
              size="medium"
            >
              <AccessTime />
            </Fab>
          </Tooltip>
        </Box>
      );
    } else {
      return (
        <Box sx={desktopPosition}>
          <Paper 
            elevation={3} 
            sx={{ 
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              pl: 1,
              pr: 0.5,
              py: 0.5,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(4px)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[6]
              }
            }}
            ref={buttonRef}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
              <AccessTime fontSize="small" color="primary" sx={{ mr: 0.5 }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {getCurrentFilterLabel()}
              </Typography>
            </Box>
            <IconButton 
              color="primary" 
              onClick={handleClick}
              aria-label={t.openFilters}
              size="small"
              sx={{ 
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                }
              }}
            >
              <FilterList fontSize="small" />
            </IconButton>
          </Paper>
        </Box>
      );
    }
  };

  return (
    <>
      {/* Filter button */}
      {renderFilterButton()}

      {/* Filter popover */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: isMobile ? 'top' : 'bottom',
          horizontal: isMobile ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: isMobile ? 'bottom' : 'top',
          horizontal: isMobile ? 'left' : 'right',
        }}
        PaperProps={{
          sx: {
            p: 2,
            maxWidth: isMobile ? '90vw' : '300px',
            borderRadius: 2,
          }
        }}
      >
        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
          {t.timeRange}
        </Typography>

        {isMobile ? (
          // Mobile view - vertical buttons
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {timeFilterOptions.map((option) => (
              <Button
                key={option.value}
                variant={timeFilter === option.value ? "contained" : "outlined"}
                color="primary"
                onClick={() => applyFilter(option.value)}
                startIcon={option.icon}
                endIcon={timeFilter === option.value ? <Check /> : null}
                fullWidth
                sx={{ justifyContent: 'flex-start', py: 1 }}
              >
                {option.label}
              </Button>
            ))}
          </Box>
        ) : (
          // Desktop view - button group
          <ButtonGroup
            orientation="vertical"
            variant="outlined"
            sx={{ width: '100%' }}
          >
            {timeFilterOptions.map((option) => (
              <Button
                key={option.value}
                variant={timeFilter === option.value ? "contained" : "outlined"}
                color="primary"
                onClick={() => applyFilter(option.value)}
                startIcon={option.icon}
                endIcon={timeFilter === option.value ? <Check /> : null}
                sx={{ justifyContent: 'flex-start', py: 1 }}
              >
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
        )}
      </Popover>
    </>
  );
};

export default FilterPanel;
